'use client'

import { FC, useState } from 'react'

import { extractAssetIdFromUrl } from '@/lib/secureAssetUtils'

import { PhnxButton } from '../Button/Button'
import styles from './SecureDownload.module.css'

interface SecureDownloadProps {
  assetId?: string
  assetTitle?: string
  assetDescription?: string
  className?: string
  isInsideStoryblok?: boolean
  allowAssetInput?: boolean
}

const SecureDownload: FC<SecureDownloadProps> = ({
  assetId: propAssetId,
  assetTitle = 'Private Asset',
  assetDescription,
  className = '',
  isInsideStoryblok = false,
  allowAssetInput = false,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Asset input states for testing mode
  const [assetInput, setAssetInput] = useState('')
  const [assetId, setAssetId] = useState(propAssetId ?? '')
  const [showAssetInput, setShowAssetInput] = useState(
    Boolean((typeof propAssetId !== 'string' || propAssetId.length === 0) && allowAssetInput)
  )

  // Only render if not in asset input mode OR if inside Storyblok
  if (allowAssetInput && !isInsideStoryblok) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.blockedMessage}>
          <h3>⚠️ Test Mode Not Available</h3>
          <p>This component is in test mode and only accessible within Storyblok CMS.</p>
        </div>
      </div>
    )
  }

  const handleAssetInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAssetInput(e.target.value)
    setError(null)
  }

  const handleProcessAsset = () => {
    if (!assetInput.trim()) {
      setError('Please enter an asset URL or ID')
      return
    }

    let processedId = assetInput.trim()

    // Check if it's a full URL or just an ID
    if (assetInput.includes('storyblok.com')) {
      const extracted = extractAssetIdFromUrl(assetInput)
      if (typeof extracted !== 'string' || extracted.length === 0) {
        setError('Could not extract asset ID from URL. Please check the format.')
        return
      }
      processedId = extracted
    }

    setAssetId(processedId)
    setShowAssetInput(false)
    setError(null)
  }

  const handleResetAsset = () => {
    setAssetInput('')
    setAssetId('')
    setShowAssetInput(true)
    setSuccess(false)
    setError(null)
  }

  const handleDirectDownload = async () => {
    if (!assetId) {
      setError('No asset ID provided')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/secure-download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assetId,
          email: 'test@findex.com.au', // Default email for internal testing
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          typeof data.error === 'string' && data.error.length > 0
            ? data.error
            : 'Failed to generate download link'
        )
      }

      // Success - initiate download
      if (typeof data.downloadUrl === 'string' && data.downloadUrl.length > 0) {
        window.open(data.downloadUrl, '_blank')
      } else {
        throw new Error('Invalid download URL received')
      }
      setSuccess(true)

      // Reset after successful download
      setTimeout(() => {
        setSuccess(false)
        if (allowAssetInput) {
          handleResetAsset()
        }
      }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  // Show asset input mode first if enabled and no asset ID
  if (showAssetInput) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.assetInputHeader}>
          <h3 className={styles.assetTitle}>Secure Download</h3>
          <div className={styles.testBadge}>Storyblok Component Only</div>
          <p className={styles.assetDescription}>
            Enter an asset URL or ID to download a private asset.
          </p>
        </div>

        <div className={styles.assetInputSection}>
          <div className={styles.formGroup}>
            <label htmlFor="assetInput" className={styles.label}>
              Asset URL or Asset ID
            </label>
            <input
              type="text"
              id="assetInput"
              value={assetInput}
              onChange={handleAssetInputChange}
              className={styles.input}
              placeholder="https://a.storyblok.com/f/123456/abc123def456/file.pdf or abc123def456"
            />
            <small className={styles.hint}>
              Paste the full Storyblok asset URL or just the asset ID
            </small>
          </div>

          {typeof error === 'string' && error.length > 0 && (
            <div className={styles.errorMessage}>{error}</div>
          )}

          <div className={styles.formActions}>
            <PhnxButton
              type="button"
              variant="primary"
              label="🚀 Process Asset"
              onClick={handleProcessAsset}
            />
          </div>
        </div>

        <div className={styles.instructions}>
          <h4>Instructions:</h4>
          <ol>
            <li>Go to the asset in the Storyblok CMS</li>
            <li>Copy the asset URL or ID</li>
            <li>Paste it above and click &ldquo;Process Asset&rdquo;</li>
            <li>Click the &ldquo;Download Asset&rdquo; button</li>
          </ol>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.successMessage}>
          <div className={styles.successIcon}>✓</div>
          <h3>Download Started</h3>
          <p>Your download should begin shortly.</p>
          {allowAssetInput && (
            <button onClick={handleResetAsset} className={styles.resetButton}>
              Download Different Asset
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.assetInfo}>
        <h3 className={styles.assetTitle}>{assetTitle}</h3>
        {typeof assetDescription === 'string' && assetDescription.length > 0 && (
          <p className={styles.assetDescription}>{assetDescription}</p>
        )}
        {allowAssetInput && assetId && (
          <div className={styles.currentAsset}>
            <p>
              <strong>Asset ID:</strong> <code>{assetId}</code>
            </p>
            <button onClick={handleResetAsset} className={styles.resetButton}>
              Download Different Asset
            </button>
          </div>
        )}
      </div>

      <div className={styles.downloadSection}>
        {typeof error === 'string' && error.length > 0 && (
          <div className={styles.errorMessage}>{error}</div>
        )}

        <div className={styles.formActions}>
          <PhnxButton
            type="button"
            variant="primary"
            label={isLoading ? 'Downloading...' : '⬇️ Download Asset'}
            onClick={handleDirectDownload}
            disabled={isLoading}
          />
        </div>

        <p className={styles.securityNote}>
          This will generate a temporary download link that expires in 1 day.
        </p>
      </div>
    </div>
  )
}

export default SecureDownload
